import { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { bootstrapCore } from './app/bootstrap';
import { createCoreClient } from './app/create-core-client';
import { FirstEntry } from './components/entry/FirstEntry';
import type { FrontendEnvironment } from './config/environment';
import type { BootstrapState } from './contracts/foundation';
import type { RecoveryReport } from './sovereign/domain';
import { SovereignProjectRepository } from './sovereign/storage';
import type { SovereignProject } from './sovereign/domain';

export interface AppProps {
  readonly environment: FrontendEnvironment;
}

const NoemirWorkspace = lazy(() =>
  import('./app/NoemirWorkspace').then((module) => ({ default: module.NoemirWorkspace })),
);

export const App = ({ environment }: AppProps) => {
  const client = useMemo(() => createCoreClient(), []);
  const [state, setState] = useState<BootstrapState>({ status: 'initializing' });
  const repository = useMemo(() => new SovereignProjectRepository(), []);
  const [recovery, setRecovery] = useState<RecoveryReport | null>(null);

  useEffect(() => {
    let active = true;

    void Promise.all([bootstrapCore(client), repository.load()]).then(([nextState, report]) => {
      if (active) {
        setState(nextState);
        setRecovery(report);
      }
    });

    return () => {
      active = false;
    };
  }, [client, repository]);

  if (state.status === 'failed') {
    return (
      <main className="startup-state startup-error" aria-labelledby="foundation-error-title">
        <p className="surface-eyebrow">System error</p>
        <h1 id="foundation-error-title">{environment.appName} could not start.</h1>
        <p>{state.error.message}</p>
        <code>{state.error.code}</code>
      </main>
    );
  }

  if (state.status === 'initializing' || recovery === null) {
    return (
      <main className="startup-state" aria-busy="true" aria-label="Noemir initialization">
        <span className="loading-mark" aria-hidden="true" />
        <span>Initializing local foundation</span>
      </main>
    );
  }

  if (recovery.project === null) {
    return (
      <FirstEntry
        onEnter={async (project: SovereignProject) => {
          await repository.save(project);
          setRecovery({ project, recovered: false, source: 'primary', issues: [] });
        }}
      />
    );
  }

  return (
    <Suspense
      fallback={
        <main className="startup-state" aria-busy="true" aria-label="Opening local system">
          <span className="loading-mark" aria-hidden="true" />
          <span>Opening local system</span>
        </main>
      }
    >
      <NoemirWorkspace
        environment={environment}
        foundation={state.foundation}
        repository={repository}
        recovery={recovery}
        exampleMode={recovery.project.projectId === 'noemir-example-system'}
      />
    </Suspense>
  );
};
